import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertCircle, CheckCircle2, Clock, XCircle } from 'lucide-react';
import {
  NAVY, PageAssu, FormuleCarte, PiecesJointes, Recapitulatif, BlocValidation,
  ChampNomCrm, titreEtape, numeroEtape
} from './FormuleAssu';

// Page du dossier personnel d'un candidat, ouverte depuis le lien unique envoyé
// sur WhatsApp. Même parcours que la page publique des conditions, en une seule
// page, sans l'étape des coordonnées : elles sont déjà sur sa fiche.

function DossierCandidatAssu() {
  const { token } = useParams();
  const API_URL = process.env.REACT_APP_API_URL || '';

  const [data, setData] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreurFatale, setErreurFatale] = useState('');
  const [erreur, setErreur] = useState('');
  const [accepte, setAccepte] = useState(false);
  const [occupe, setOccupe] = useState(null);
  const [manquants, setManquants] = useState([]);

  const dateFr = (d) => d ? new Date(d).toLocaleString('fr-FR', {
    day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
  }) : null;

  const charger = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/candidats-assu/dossier/${token}`);
      setData(res.data);
      setAccepte(!!res.data.conditions_acceptees);
    } catch (e) {
      setErreurFatale(e.response?.data?.error || 'Ce lien n\'est pas valide. Demandez-nous un nouveau lien.');
    } finally {
      setChargement(false);
    }
  }, [API_URL, token]);

  useEffect(() => { charger(); }, [charger]);

  const choisirFormule = async (formuleId) => {
    if (formuleId === data.formule_id) return;
    setOccupe('formule');
    setErreur('');
    try {
      const res = await axios.post(`${API_URL}/api/candidats-assu/dossier/${token}/formule`, { formule_id: formuleId });
      setData(prev => ({ ...prev, ...res.data }));
      setManquants([]);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur lors de l\'enregistrement de votre choix');
    } finally {
      setOccupe(null);
    }
  };

  const valider = async () => {
    setOccupe('validation');
    setErreur('');
    try {
      const res = await axios.post(`${API_URL}/api/candidats-assu/dossier/${token}/soumettre`, {
        conditions_acceptees: accepte
      });
      setData(prev => ({ ...prev, ...res.data }));
      setManquants([]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur lors de l\'envoi de votre candidature');
      setManquants(e.response?.data?.manquants || []);
    } finally {
      setOccupe(null);
    }
  };

  if (chargement) {
    return (
      <PageAssu titre="Votre dossier d'adhésion" sousTitre="Un instant…">
        <div className="dca-card" style={{ textAlign: 'center', color: '#5f6b85' }}>
          <Loader2 size={26} className="dca-spin" style={{ color: NAVY }} />
          <div style={{ marginTop: 10 }}>Chargement de votre dossier…</div>
        </div>
      </PageAssu>
    );
  }

  if (erreurFatale) {
    return (
      <PageAssu titre="Votre dossier d'adhésion" sousTitre="Lien indisponible">
        <div className="dca-card" style={{ textAlign: 'center' }}>
          <XCircle size={34} style={{ color: '#c62828' }} />
          <h2 style={{ margin: '12px 0 8px', fontSize: 19 }}>Lien indisponible</h2>
          <p style={{ color: '#5f6b85', margin: 0 }}>{erreurFatale}</p>
        </div>
      </PageAssu>
    );
  }

  // ---- Candidature déjà envoyée : le candidat relit ce qu'il a transmis ----
  if (['soumis', 'valide'].includes(data.statut)) {
    const validee = data.statut === 'valide';
    const aCompleter = (data.documents_requis || []).filter(
      t => !(data.documents || []).some(d => d.type_doc === t.id)
    );
    return (
      <PageAssu
        titre="Votre candidature"
        sousTitre={validee ? 'Votre dossier est validé' : 'Nous vérifions vos pièces'}
      >
        <div className="dca-card" style={{ textAlign: 'center' }}>
          {validee
            ? <CheckCircle2 size={36} style={{ color: '#2e9e5b' }} />
            : <Clock size={34} style={{ color: '#f0a020' }} />}
          <h2 style={{ margin: '12px 0 8px', fontSize: 20 }}>
            {validee ? 'Dossier validé' : 'Candidature envoyée'}
          </h2>
          <p style={{ color: '#5f6b85', maxWidth: 470, margin: '0 auto' }}>
            {validee
              ? <>Félicitations {data.candidat.nom}, votre dossier est validé. Nous vous contactons pour la suite.</>
              : <>Merci {data.candidat.nom}. Votre candidature et vos pièces nous sont bien parvenues.
                  Nous les vérifions et revenons vers vous sur WhatsApp.</>}
          </p>
        </div>
        <Recapitulatif data={data} dateEnvoi={dateFr(data.soumis_at)} />

        {!validee && (
          <>
            <ChampNomCrm
              token={token}
              apiUrl={API_URL}
              valeur={data.candidat?.nom_crm}
              onMaj={(maj) => setData(prev => ({ ...prev, ...maj }))}
            />
            <div style={titreEtape}>
              {aCompleter.length > 0 ? 'Complétez vos pièces' : 'Ajouter ou corriger une pièce'}
            </div>
            {aCompleter.length > 0 && (
              <div className="dca-card" style={{ borderColor: '#e6c98a', background: '#fdfaf3' }}>
                <div style={{ display: 'flex', gap: 10 }}>
                  <AlertCircle size={20} style={{ color: '#b8860b', flexShrink: 0, marginTop: 2 }} />
                  <div style={{ fontSize: 14.5 }}>
                    Il nous manque {aCompleter.map(t => t.label).join(', ')}.
                    Envoyez-{aCompleter.length === 1 ? 'la' : 'les'} ci-dessous, votre dossier sera complet.
                  </div>
                </div>
              </div>
            )}
            <PiecesJointes
              token={token}
              data={data}
              onMaj={(maj) => setData(prev => ({ ...prev, ...maj }))}
              apiUrl={API_URL}
            />
          </>
        )}
      </PageAssu>
    );
  }

  const manquantes = (data.documents_requis || []).filter(
    t => !(data.documents || []).some(d => d.type_doc === t.id)
  );

  return (
    <PageAssu
      titre="Votre dossier d'adhésion"
      sousTitre={data.candidat?.nom ? `Bonjour ${data.candidat.nom}` : 'Votre dossier'}
    >
      {data.statut === 'refuse' && data.motif_refus && (
        <div className="dca-card" style={{ borderColor: '#e0a3a3', background: '#fdf6f6' }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <AlertCircle size={20} style={{ color: '#c62828', flexShrink: 0, marginTop: 2 }} />
            <div>
              <div style={{ fontWeight: 800, color: '#c62828', marginBottom: 4 }}>Dossier à corriger</div>
              <div style={{ fontSize: 14.5 }}>{data.motif_refus}</div>
              <div style={{ fontSize: 13.5, color: '#5f6b85', marginTop: 6 }}>
                Corrigez ce qui est demandé, puis validez à nouveau.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 1 — la formule */}
      <div style={titreEtape}><span style={numeroEtape}>1</span> Choisissez votre formule</div>

      {data.formules.length === 0 ? (
        <div className="dca-card" style={{ borderColor: '#e6c98a', background: '#fdfaf3' }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <AlertCircle size={20} style={{ color: '#b8860b', flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 14.5 }}>
              Nos conditions ne sont pas encore publiées sur cette page. Contactez-nous sur WhatsApp,
              nous vous les transmettons directement.
            </div>
          </div>
        </div>
      ) : (
        data.formules.map(f => (
          <FormuleCarte
            key={f.id}
            formule={f}
            choisie={f.id === data.formule_id}
            onChoisir={choisirFormule}
            occupe={occupe === 'formule'}
          />
        ))
      )}

      {/* 2 — les pièces */}
      <div style={{ ...titreEtape, opacity: data.formule_id ? 1 : 0.45 }}>
        <span style={numeroEtape}>2</span> Envoyez vos documents
      </div>
      {data.formule_id ? (
        <PiecesJointes
          token={token}
          data={data}
          onMaj={(maj) => { setData(prev => ({ ...prev, ...maj })); setManquants([]); }}
          manquants={manquants}
          apiUrl={API_URL}
        />
      ) : (
        <div className="dca-card" style={{ color: '#5f6b85', fontSize: 14.5 }}>
          Choisissez d'abord votre formule, la liste des pièces s'affichera ici.
        </div>
      )}

      {/* 3 — le récapitulatif et la validation */}
      <div style={{ ...titreEtape, opacity: data.formule_id ? 1 : 0.45 }}>
        <span style={numeroEtape}>3</span> Vérifiez et validez
      </div>
      <ChampNomCrm
        token={token}
        apiUrl={API_URL}
        valeur={data.candidat?.nom_crm}
        onMaj={(maj) => setData(prev => ({ ...prev, ...maj }))}
      />

      <Recapitulatif data={data} />

      <BlocValidation
        peutValider={!!data.formule_id}
        accepte={accepte}
        setAccepte={setAccepte}
        erreur={erreur}
        occupe={occupe === 'validation'}
        onValider={valider}
        avertissement={
          !data.formule_id
            ? 'Cochez la formule qui vous convient pour pouvoir valider.'
            : manquantes.length > 0
              ? `Vous validez sans ${manquantes.length === 1 ? 'la pièce suivante' : 'les pièces suivantes'} : ${manquantes.map(m => m.label).join(', ')}. Vous pourrez ${manquantes.length === 1 ? 'l\'' : 'les '}envoyer plus tard depuis ce lien.`
              : null
        }
      />
    </PageAssu>
  );
}

export default DossierCandidatAssu;
